import { UseFormReturn } from 'react-hook-form'
import { WidgetFormValues } from '@/lib/widget-schema'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl,
	FormDescription,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ColorPicker } from '@/components/widget-builder/ColorPicker' // Assuming you have this or use the ui one
import { PositionPicker } from '@/components/widget-builder/PositionPicker' // Assuming you have this or use the ui one
import { Switch } from '@/components/ui/switch'

import { Crown } from 'lucide-react'

import { PremiumFeature } from '@/components/ui/premium-feature'
import { useFeatureLimit } from '@/hooks/useFeatureLimit'
import { ThemeSelector } from '@/components/widget-builder/themes/ThemeSelector'
import { useTranslation } from '@/hooks/useTranslation'

interface Step3Props {
	form: UseFormReturn<WidgetFormValues>
	isPremium: boolean
}

export function Step3_Appearance({ form, isPremium }: Step3Props) {
	const { t } = useTranslation()
	const brandingLimit = useFeatureLimit('can_remove_branding')
	const canRemoveBranding = isPremium

	return (
		<Card>
			<CardHeader>
				<CardTitle>{t('widgetBuilder.step3.title')}</CardTitle>
				<CardDescription>
					{t('widgetBuilder.step3.description')}
				</CardDescription>
			</CardHeader>




			<CardContent className="space-y-6">

			<div className="space-y-4 pt-4 border-t">
					<h3 className="text-md font-semibold">{t('widgetBuilder.step3.feedbackButton')}</h3>
					<FormDescription>
						{t('widgetBuilder.step3.feedbackButtonDescription')}
					</FormDescription>

					<div className="space-y-4">
						<FormField
							control={form.control}
							name="feedbackButton.text"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('widgetBuilder.step3.buttonText')}</FormLabel>
									<FormControl>
										<Input placeholder={t('widgetBuilder.step3.buttonTextPlaceholder')} {...field} />
									</FormControl>
								</FormItem>
							)}
						/>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="feedbackButton.color"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<ColorPicker
												value={field.value}
												onChange={field.onChange}
												label={t('widgetBuilder.step3.buttonColor')}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="feedbackButton.textColor"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<ColorPicker
												value={field.value}
												onChange={field.onChange}
												label={t('widgetBuilder.step3.buttonTextColor')}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
						</div>
						<FormField
							control={form.control}
							name="widgetTriggerSize"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Closed widget size</FormLabel>
									<FormControl>
										<div className="flex gap-2">
											<Button
												type="button"
												variant={field.value === 's' ? 'default' : 'outline'}
												onClick={() => field.onChange('s')}>
												Small
											</Button>
											<Button
												type="button"
												variant={field.value === 'm' ? 'default' : 'outline'}
												onClick={() => field.onChange('m')}>
												Medium
											</Button>
											<Button
												type="button"
												variant={field.value === 'l' ? 'default' : 'outline'}
												onClick={() => field.onChange('l')}>
												Large
											</Button>
										</div>
									</FormControl>
									<FormDescription>
										Controls the closed trigger size (`data-size`: s, m, l)
									</FormDescription>
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="widgetTriggerIcon"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Closed widget icon URL</FormLabel>
									<FormControl>
										<Input
											placeholder="https://example.com/icon.svg"
											value={field.value ?? ''}
											onChange={field.onChange}
										/>
									</FormControl>
									<FormDescription>
										Overrides the default icon (`data-icon`)
									</FormDescription>
								</FormItem>
							)}
						/>
					</div>
				</div>

				{/* Theme Selector */}
				<ThemeSelector form={form} />

				<FormField
					control={form.control}
					name="position"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('widgetBuilder.step3.widgetPosition')}</FormLabel>
							<FormDescription>
								{t('widgetBuilder.step3.widgetPositionDescription')}
							</FormDescription>
							<FormControl>
								<PositionPicker value={field.value} onChange={field.onChange} />
							</FormControl>
						</FormItem>
					)}
				/>

				<div className="space-y-4 pt-4 border-t">
					<h3 className="text-md font-semibold">{t('widgetBuilder.step3.colors')}</h3>
					<FormDescription>
						{t('widgetBuilder.step3.colorsDescription')}
					</FormDescription>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<FormField
							control={form.control}
							name="primaryColor"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<ColorPicker
											value={field.value}
											onChange={field.onChange}
											label={t('widgetBuilder.step3.primaryColor')}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="gradientEndColor"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<ColorPicker
											value={field.value || ''}
											onChange={(val) => field.onChange(val || null)}
											label={t('widgetBuilder.step3.headerGradientEnd')}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="backgroundColor"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<ColorPicker
											value={field.value}
											onChange={field.onChange}
											label={t('widgetBuilder.step3.backgroundColor')}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="textColor"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<ColorPicker
											value={field.value}
											onChange={field.onChange}
											label={t('widgetBuilder.step3.textColor')}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
					</div>
				</div>

				

				<div className="space-y-4 pt-4 border-t">
					<h3 className="text-md font-semibold">{t('widgetBuilder.step3.branding')}</h3>
					<PremiumFeature
						isLocked={!canRemoveBranding}
						message={t('widgetBuilder.step3.upgradeToProToRemove')}>
						<FormField
							control={form.control}
							name="showBranding"
							render={({ field }) => (
								<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
									<div className="space-y-0.5">
										<FormLabel className="flex items-center gap-2">
											{t('widgetBuilder.step3.showPoweredByBranding')}
											{!canRemoveBranding && (
												<Crown className="h-4 w-4 text-yellow-500" />
											)}
										</FormLabel>
										<FormDescription>
											{canRemoveBranding
												? t('widgetBuilder.step3.toggleFeedbaskBranding')
												: t('widgetBuilder.step3.upgradeToProToRemoveBranding')}
										</FormDescription>
									</div>
									<Switch
										checked={field.value}
										onCheckedChange={field.onChange}
										disabled={!canRemoveBranding}
										aria-label={t('widgetBuilder.step3.toggleBrandingAriaLabel')}
									/>
								</FormItem>
							)}
						/>
					</PremiumFeature>
				</div>
			</CardContent>
		</Card>
	)
}
